/**
 * As linhas que o histórico mostra de cada registro (E137.F3, GUS-160), lidas dos dados que o
 * servidor gravou na trilha. Regra pura: nenhum dado do servidor vira texto da tela sem passar por
 * um rótulo; o hash aparece abreviado (o inteiro fica no registro); o nome do serviço externo não
 * aparece, só que a versão dele não foi fornecida.
 */
import { ptBR } from '@/i18n/pt-BR';
import { motivoDaRecusa, textoDoMotivo } from '@/lib/recusa_do_arquivo';
import type { EventoNoHistoricoData, TipoDoEvento } from '@/lib/historico_do_caso';

const t = ptBR.historicoDoCaso.detalhe;

type Dados = Record<string, unknown>;

function texto(dados: Dados, chave: string): string | null {
  const valor = dados[chave];
  return typeof valor === 'string' && valor !== '' ? valor : null;
}

function numero(dados: Dados, chave: string): number | null {
  const valor = dados[chave];
  return typeof valor === 'number' ? valor : null;
}

function curto(sha: string | null): string {
  return sha === null ? '—' : `${sha.slice(0, 12)}…`;
}

/** E184.F4: a conclusão do par em quatro palavras (o degrau da escala fica no parecer). */
function daMorfologia(compatibilidade: string | null): string {
  const r = t.resultadoDaMorfologia;
  if (compatibilidade === null) return '—';
  if (compatibilidade.startsWith('mesma_origem')) return r.compativel;
  if (compatibilidade.startsWith('origem_distinta')) return r.divergente;
  if (compatibilidade === 'nao_avaliavel') return r.nao_avaliavel;
  return r.inconclusivo;
}

function noRotulo<T extends Record<string, string>>(mapa: T, chave: string | null): string | null {
  return chave !== null && chave in mapa ? (mapa[chave as keyof T] ?? null) : null;
}

function doPedidoDaConsulta(dados: Dados): string {
  const consulta = noRotulo(t.consultaPorProvedor, texto(dados, 'provedor')) ?? '—';
  return t.consultaDoPedido.replace('{consulta}', consulta);
}

function doRecebimento(dados: Dados): string[] {
  const linhas = [t.manifesto.replace('{sha}', curto(texto(dados, 'manifesto_sha256')))];
  linhas.push(t.arquivosNovos.replace('{n}', String(numero(dados, 'arquivos_novos') ?? 0)));
  if (dados.retentativa_sem_arquivo_novo === true) linhas.push(t.retentativa);
  else if (texto(dados, 'complemento_de') !== null) linhas.push(t.complemento);
  return linhas;
}

function doExame(dados: Dados): string[] {
  const linhas = [t.regua.replace('{regua}', texto(dados, 'regua') ?? '—')];
  const declarado = dados.hash_declarado as Dados | null | undefined;
  if (declarado !== null && declarado !== undefined) {
    const fonte = noRotulo(t.fonte, texto(declarado, 'fonte'));
    const situacao = t.hashDeclarado.replace(
      '{situacao}',
      noRotulo(t.situacaoDoCodigo, texto(declarado, 'situacao')) ?? '—',
    );
    linhas.push(fonte === null ? situacao : `${situacao} · ${fonte}`);
  }
  const consultas = Array.isArray(dados.consultas_externas) ? dados.consultas_externas.length : 0;
  if (consultas > 0) linhas.push(t.consultas.replace('{n}', String(consultas)));
  linhas.push(t.resultado.replace('{sha}', curto(texto(dados, 'resultado_sha256'))));
  return linhas;
}

function codigoDoTexto(sha: string | null): string {
  return sha === null ? t.textoOriginal : sha.slice(0, 8);
}

/** E171.F3.C02: a edição do editor manual — saída, seção e trecho, a ação, a versão do texto antes
 * e depois (o código que o editor mostra) e o fundamento. Os registros gravados antes da E171.F1.C01
 * não têm seção, versão nem fundamento: ficam a saída e a ação. E184.F1: a versão da análise em que
 * a edição foi gravada (`rodada`, gravada desde a E160.F2). */
function doEditor(dados: Dados, saida: string): string[] {
  const acao = noRotulo(t.acaoDaEdicao, texto(dados, 'acao'));
  const rodada = numero(dados, 'rodada');
  const analise = rodada === null ? null : t.analiseDaEdicao.replace('{n}', String(rodada));
  const trecho = [saida, texto(dados, 'secao'), texto(dados, 'rotulo'), acao, analise];
  const linhas = [trecho.filter((p): p is string => p !== null).join(' · ')];
  if ('versao_depois' in dados) {
    linhas.push(
      t.versaoDoTexto
        .replace('{depois}', codigoDoTexto(texto(dados, 'versao_depois')))
        .replace('{antes}', codigoDoTexto(texto(dados, 'versao_antes'))),
    );
  }
  const fundamento = texto(dados, 'fundamento');
  if (fundamento !== null) linhas.push(t.fundamentoDaEdicao.replace('{texto}', fundamento));
  return linhas;
}

function daEdicao(dados: Dados): string[] {
  const saida = noRotulo(t.saidaDaEdicao, texto(dados, 'saida'));
  if (saida !== null) return doEditor(dados, saida);
  const linhas: string[] = [];
  const campos = Array.isArray(dados.campos) ? dados.campos.join(', ') : texto(dados, 'campo');
  if (campos !== null) linhas.push(t.campo.replace('{campo}', campos));
  const revisao = numero(dados, 'revisao');
  if (revisao !== null) linhas.push(t.revisao.replace('{n}', String(revisao)));
  const origem = noRotulo(t.origem, texto(dados, 'origem'));
  if (origem !== null) linhas.push(origem);
  return linhas;
}

function daCustodia(evento: EventoNoHistoricoData): string[] {
  const dados = evento.dados;
  if (evento.tipo === 'conferencia_registrada') {
    return [
      t.conferencia
        .replace('{conferem}', String(numero(dados, 'conferem') ?? 0))
        .replace('{total}', String(numero(dados, 'total') ?? 0)),
    ];
  }
  const rotulo =
    evento.tipo === 'ocorrencia_aberta'
      ? noRotulo(t.situacao, texto(dados, 'situacao'))
      : noRotulo(t.acao, texto(dados, 'acao'));
  const nota = texto(dados, 'nota');
  return [rotulo, nota].filter((l): l is string => l !== null);
}

function daConferencia(dados: Dados): string[] {
  const resultado = noRotulo(t.resultadoDaConferencia, texto(dados, 'resultado')) ?? '—';
  const linhas = [
    t.conferido
      .replace('{resultado}', resultado)
      .replace('{sha}', curto(texto(dados, 'sha256_apresentado'))),
  ];
  const versao = numero(dados, 'versao');
  if (versao !== null) {
    linhas.push(
      t.referencia
        .replace('{versao}', String(versao))
        .replace('{sha}', curto(texto(dados, 'sha256_registrado'))),
    );
  }
  return linhas;
}

function pct(valor: unknown): string {
  return typeof valor === 'number' ? String(Math.round(valor * 100)) : '—';
}

/** E141.F1 (ADR-0079 E.26): o ajuste do recorte — a foto, a geometria nova ou "desfeito". */
function doRecorte(dados: Dados): string[] {
  const linhas: string[] = [];
  const grupo = noRotulo(t.grupoDaFoto, texto(dados, 'grupo'));
  if (grupo !== null) linhas.push(grupo);
  const giro = String(numero(dados, 'giro') ?? 0);
  const caixa = dados.caixa as Dados | null | undefined;
  if (dados.desfeito === true) linhas.push(t.recorteDesfeito);
  else if (caixa === null || caixa === undefined) {
    linhas.push(t.recorteSoGiro.replace('{giro}', giro));
  } else {
    linhas.push(
      t.recorteCaixa
        .replace('{w}', pct(caixa.w))
        .replace('{h}', pct(caixa.h))
        .replace('{x}', pct(caixa.x))
        .replace('{y}', pct(caixa.y))
        .replace('{giro}', giro),
    );
  }
  return linhas;
}

/** E216.F4 (GUS-317 item 3): o arquivo excluído, o SHA-256 guardado, o motivo e o fundamento. */
function doDocumentoExcluido(dados: Dados): string[] {
  const motivo = noRotulo(t.motivoDaExclusao, texto(dados, 'motivo'));
  const fundamento = texto(dados, 'fundamento');
  return [
    t.arquivoExcluido
      .replace('{arquivo}', texto(dados, 'arquivo') ?? '—')
      .replace('{sha}', curto(texto(dados, 'sha256'))),
    ...(motivo !== null ? [motivo] : []),
    ...(fundamento !== null ? [t.fundamentoDaExclusao.replace('{fundamento}', fundamento)] : []),
  ];
}

/** E214.F1 (GUS-314): a área recortada (página e caixa), o destino e os dois hashes. */
function doDocumentoRecortado(dados: Dados): string[] {
  const area = (dados.area ?? {}) as Dados;
  const destino = noRotulo(t.destinoDoRecorte, texto(dados, 'destino'));
  return [
    t.documentoRecortado
      .replace('{pagina}', String(numero(dados, 'pagina') ?? '—'))
      .replace('{w}', pct(area.w))
      .replace('{h}', pct(area.h))
      .replace('{x}', pct(area.x))
      .replace('{y}', pct(area.y)),
    ...(destino !== null ? [destino] : []),
    t.recorteDoOriginal
      .replace('{recorte}', curto(texto(dados, 'sha256_do_recorte')))
      .replace('{original}', curto(texto(dados, 'sha256_do_original'))),
  ];
}

/** A data civil `AAAA-MM-DD` como a tela mostra (`DD/MM/AAAA`). */
// E155.F1 (GUS-192, ADR-0105 §2.5): a informação revista na fonte, pelo nome que a ficha usa; o
// resultado e a origem por rótulo; os documentos reabertos só quando houve.
function daReleitura(dados: Dados): string[] {
  const linhas = [
    t.leituraRevista
      .replace('{campo}', noRotulo(ptBR.fichaDaLeitura.campo, texto(dados, 'campo')) ?? '—')
      .replace('{resultado}', noRotulo(t.resultadoDaReleitura, texto(dados, 'resultado')) ?? '—')
      .replace('{origem}', noRotulo(t.origemDaReleitura, texto(dados, 'origem')) ?? '—'),
  ];
  const reabertas = numero(dados, 'pecas_reabertas');
  if (reabertas !== null && reabertas > 0) {
    linhas.push(t.pecasReabertas.replace('{n}', String(reabertas)));
  }
  return linhas;
}

function dataCivil(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return dia !== undefined && mes !== undefined ? `${dia}/${mes}/${ano}` : iso;
}

/** E144.F3: o pedido do cliente como ficou depois da mudança (prazo, urgência, observação). */
function doPedido(dados: Dados): string[] {
  const prazo = texto(dados, 'prazo_processual');
  const hora = texto(dados, 'prazo_processual_hora');
  const quando =
    prazo === null ? null : `${dataCivil(prazo)}${hora !== null ? ` ${hora.slice(0, 5)}` : ''}`;
  const linhas = [
    quando === null ? t.semPrazo : t.prazoDoPedido.replace('{prazo}', quando),
    dados.urgencia_solicitada === true
      ? t.urgenciaPedida.replace('{motivo}', texto(dados, 'motivo_da_urgencia') ?? '—')
      : t.semUrgencia,
  ];
  const observacao = texto(dados, 'observacao');
  if (observacao !== null) linhas.push(t.observacaoDoPedido.replace('{texto}', observacao));
  return linhas;
}

type Montador = (evento: EventoNoHistoricoData, seqPorId: ReadonlyMap<string, number>) => string[];

const _SEM_VERSAO: Montador = () => [t.versaoNaoFornecida];

const _MONTADORES: Record<TipoDoEvento, Montador> = {
  recebimento_selado: (e) => doRecebimento(e.dados),
  exame_verificado: (e) => doExame(e.dados),
  laudo_editado: (e) => daEdicao(e.dados),
  conferencia_registrada: (e) => daCustodia(e),
  ocorrencia_aberta: (e) => daCustodia(e),
  ocorrencia_resolvida: (e) => daCustodia(e),
  documento_emitido: (e) => [
    t.documento
      .replace('{nome}', texto(e.dados, 'nome') ?? '—')
      .replace('{sha}', curto(texto(e.dados, 'sha256'))),
  ],
  laudo_assinado: (e) => [
    t.assinatura.replace('{sha}', curto(texto(e.dados, 'documento_sha256'))),
    ...(e.forma_da_assinatura ? [t.formaDaAssinatura[e.forma_da_assinatura]] : []),
  ],
  documento_conferido: (e) => daConferencia(e.dados),
  documento_divergente: (e) => [
    t.divergente
      .replace('{registrado}', curto(texto(e.dados, 'sha256_registrado')))
      .replace('{encontrado}', curto(texto(e.dados, 'sha256_encontrado'))),
  ],
  ficha_da_leitura_gravada: (e) => [
    t.leitura
      .replace('{versao}', String(numero(e.dados, 'versao') ?? '—'))
      .replace('{campos}', String(numero(e.dados, 'campos') ?? '—'))
      .replace('{sha}', curto(texto(e.dados, 'sha256'))),
  ],
  leitura_conferida: (e) => [
    t.conferenciaDaLeitura
      .replace('{ficha}', String(numero(e.dados, 'ficha_versao') ?? '—'))
      .replace('{estado}', noRotulo(t.estadoDaConferencia, texto(e.dados, 'estado')) ?? '—')
      .replace('{falhas}', String(numero(e.dados, 'falhas_tecnicas') ?? '—')),
  ],
  leitura_retomada: (e) => [
    t.retomada
      .replace('{n}', String(numero(e.dados, 'pendencias') ?? '—'))
      .replace('{origem}', noRotulo(t.origemDaRetomada, texto(e.dados, 'origem')) ?? '—'),
  ],
  limitacao_da_leitura_registrada: (e) => {
    const pagina = numero(e.dados, 'pagina_no_original');
    return [
      pagina === null
        ? t.limitacaoSemPagina
        : t.limitacaoNaPagina.replace('{pagina}', String(pagina)),
    ];
  },
  fotografia_recortada: (e) => doRecorte(e.dados),
  pedido_atualizado: (e) => doPedido(e.dados),
  acesso_por_qr_emitido: (e) => {
    const versao = numero(e.dados, 'versao');
    return versao === null ? [] : [t.versaoDoAcesso.replace('{versao}', String(versao))];
  },
  acesso_por_qr_revogado: () => [],
  arquivo_recusado: (e) => [textoDoMotivo(motivoDaRecusa(texto(e.dados, 'motivo')))],
  etapa_conferida: (e) => [
    t.etapaConferida
      .replace('{etapa}', noRotulo(t.etapaDoFluxo, texto(e.dados, 'etapa')) ?? '—')
      .replace('{liberacao}', noRotulo(t.liberacaoDaEtapa, texto(e.dados, 'liberacao')) ?? '—')
      .replace('{versao}', String(numero(e.dados, 'versao') ?? '—')),
  ],
  etapa_retomada: (e) => [doPedidoDaConsulta(e.dados)],
  limitacao_da_etapa_registrada: (e) => [doPedidoDaConsulta(e.dados)],
  assinatura_reconciliada: (e) => [
    t.assinaturaReconciliada.replace('{sha}', curto(texto(e.dados, 'sha256'))),
  ],
  mensagem_do_caso: (e) => [
    t.mensagemDoCaso
      .replace('{tipo}', noRotulo(t.tipoDaMensagem, texto(e.dados, 'tipo')) ?? '—')
      .replace('{lado}', noRotulo(t.ladoDaMensagem, texto(e.dados, 'lado')) ?? '—'),
  ],
  leitura_revista: (e) => daReleitura(e.dados),
  envio_vinculado: (e) => [
    t.envioVinculado.replace('{codigo}', texto(e.dados, 'caso_de_entrada') ?? '—'),
  ],
  origem_informada: (e) => {
    const escritorio = texto(e.dados, 'escritorio_requerente');
    const fonte = texto(e.dados, 'fonte');
    if (fonte === 'reavaliacao_da_leitura') {
      const retirado = texto(e.dados, 'banco_retirado') ?? '—';
      return [t.origemRetirada.replace('{banco}', retirado), t.origemPelaReavaliacao];
    }
    return [
      t.origemBanco.replace('{banco}', texto(e.dados, 'banco') ?? '—'),
      ...(escritorio ? [t.origemEscritorio.replace('{escritorio}', escritorio)] : []),
      ...(fonte === 'leitura_da_ccb' ? [t.origemPelaLeitura] : []),
      ...(fonte === 'equipe' ? [t.origemPelaEquipe] : []),
    ];
  },
  analise_morfologica_refeita: (e) => [
    t.morfologicaNova.replace('{resultado}', daMorfologia(texto(e.dados, 'compatibilidade'))),
    t.morfologicaAnterior.replace(
      '{resultado}',
      daMorfologia(texto(e.dados, 'compatibilidade_anterior')),
    ),
  ],
  documento_recortado: (e) => doDocumentoRecortado(e.dados),
  documento_excluido: (e) => doDocumentoExcluido(e.dados),
  // E224.F2: o motivo que a equipe escreveu ao cancelar.
  caso_cancelado: (e) => [
    t.motivoDoCancelamento.replace('{motivo}', texto(e.dados, 'motivo') ?? '—'),
  ],
  dado_revisto: (e) => [
    ...[noRotulo(t.dadoRevisto, texto(e.dados, 'alvo'))].filter((l): l is string => l !== null),
    t.dadoRevistoNaTabela,
  ],
  ccb_divergente_mantida: (e) => [
    t.ccbInformada.replace('{numero}', texto(e.dados, 'numero') ?? '—'),
    t.ccbLida.replace('{lida}', texto(e.dados, 'lida') ?? '—'),
  ],
  certificacao_revogada: (e) => {
    const forma = noRotulo(t.formaDaAssinatura, texto(e.dados, 'forma_da_assinatura'));
    return [
      t.revogada.replace('{sha}', curto(texto(e.dados, 'documento_sha256'))),
      ...(forma ? [forma] : []),
    ];
  },
  narrativa_gerada: _SEM_VERSAO,
  pre_analise_executada: _SEM_VERSAO,
  narrativa_aprovada: () => [],
  narrativa_reaberta: () => [],
  evento_corrigido: (e, seqPorId) => [
    t.corrige
      .replace('{seq}', String(seqPorId.get(e.corrige_evento_id ?? '') ?? '—'))
      .replace('{motivo}', texto(e.dados, 'motivo') ?? ''),
  ],
};

export function linhasDoEvento(
  evento: EventoNoHistoricoData,
  seqPorId: ReadonlyMap<string, number>,
): string[] {
  return _MONTADORES[evento.tipo](evento, seqPorId);
}
